import { useCallback, useRef, type HTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, type TargetAndTransition } from 'framer-motion'

import { useEscapeKey, useFocusTrap, useLockBodyScroll, useOnClickOutside } from '@/hooks'
import { cn } from '@/lib/utils'

export type ModalPlacement = 'center' | 'left' | 'right' | 'top' | 'bottom'

interface ModalPlacementConfig {
  wrapper: string
  overlay: string
  panel: string
  initial: TargetAndTransition
  animate: TargetAndTransition
  exit: TargetAndTransition
}

const placementConfig: Record<ModalPlacement, ModalPlacementConfig> = {
  center: {
    wrapper: 'flex items-center justify-center p-4',
    overlay: 'bg-black/70 backdrop-blur-sm',
    panel: 'relative w-full max-w-lg rounded-2xl',
    initial: { opacity: 0, scale: 0.96, y: 12 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.96, y: 12 },
  },
  left: {
    wrapper: 'flex',
    overlay: 'bg-black/70 backdrop-blur-sm',
    panel: 'relative inset-y-0 left-0 h-full w-80 max-w-[85vw] rounded-r-2xl',
    initial: { x: '-100%' },
    animate: { x: 0 },
    exit: { x: '-100%' },
  },
  right: {
    wrapper: 'flex',
    overlay: 'bg-black/70 backdrop-blur-sm',
    panel: 'relative inset-y-0 right-0 h-full w-80 max-w-[85vw] rounded-l-2xl',
    initial: { x: '100%' },
    animate: { x: 0 },
    exit: { x: '100%' },
  },
  top: {
    wrapper: 'flex',
    overlay: 'bg-black/70 backdrop-blur-sm',
    panel: 'relative inset-x-0 top-0 h-80 max-h-[85vh] w-full rounded-b-2xl',
    initial: { y: '-100%' },
    animate: { y: 0 },
    exit: { y: '-100%' },
  },
  bottom: {
    wrapper: 'flex',
    overlay: 'bg-black/70 backdrop-blur-sm',
    panel: 'relative inset-x-0 bottom-0 h-80 max-h-[85vh] w-full rounded-t-2xl',
    initial: { y: '100%' },
    animate: { y: 0 },
    exit: { y: '100%' },
  },
}

export interface ModalShellProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean
  onOpenChange: (open: boolean) => void
  placement?: ModalPlacement
  panelClassName?: string
  overlayClassName?: string
  titleId?: string
  descriptionId?: string
}

function ModalShell({
  open,
  onOpenChange,
  placement = 'center',
  panelClassName,
  overlayClassName,
  titleId,
  descriptionId,
  className,
  children,
}: ModalShellProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const config = placementConfig[placement]

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange])

  useLockBodyScroll(open)
  useEscapeKey(handleClose, open)
  useOnClickOutside(handleClose, open)
  useFocusTrap(open, panelRef)

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className={cn('fixed inset-0 z-50', config.wrapper, className)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            aria-hidden
            onClick={handleClose}
            className={cn('absolute inset-0', config.overlay, overlayClassName)}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            initial={config.initial}
            animate={config.animate}
            exit={config.exit}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={cn(
              'border border-border bg-card shadow-soft',
              config.panel,
              panelClassName,
            )}
          >
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}

export { ModalShell }
